import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/contexts/LocaleContext";
import { translateUnitsTo } from "@/lib/measurementConverter";
import { evaluateRecipeQuality } from "@/lib/recipeQualityGate";
import { sortByCuisineAffinity } from "@/lib/cuisineOrigin";
import { buscarTodas } from "@/lib/paginacao";

const _qualityReported = new Set<string>();

// ─── Lite translation map (title + tags only) for listing pages ───────────────
// Much smaller payload than the full map — doesn't load ingredients/preparation.
export function useRecipeTranslationsTitleMap() {
  const { language } = useLocale();

  return useQuery({
    queryKey: ["recipe-translations-titles", language],
    queryFn: async () => {
      // Era `.range(0, 1999)` com um comentário dizendo "cobre até 2000
      // receitas". Cobria — até o acervo passar de 2.000. A partir daí as
      // últimas ficavam sem tradução, sem erro nenhum aparecer.
      const data = await buscarTodas<any>(
        "recipe_translations",
        "recipe_id, title, tags",
        (q) => q.eq("language", language),
      );
      const map = new Map<string, any>();
      data.forEach((t: any) => map.set(t.recipe_id, t));
      return map;
    },
    staleTime: 1000 * 60 * 60,     // 1 hour — titles change rarely
    gcTime:    1000 * 60 * 60 * 4, // keep in memory 4 hours
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

/**
 * Lightweight version of useLocalizedRecipes for listing pages.
 * Only overlays `title` and `tags` — skips ingredients/preparation/quality gate.
 * Use this instead of useLocalizedRecipes on any page that doesn't show full recipe content.
 */
export function useLocalizedRecipesTitles<T extends { id: string }>(
  recipes: T[] | null | undefined,
): T[] | null | undefined {
  const { language } = useLocale();
  const { data: titlesMap } = useRecipeTranslationsTitleMap();

  return useMemo(() => {
    if (!recipes) return recipes;
    if (!titlesMap || titlesMap.size === 0) return recipes;

    return recipes.map((recipe) => {
      const t = titlesMap.get(recipe.id);
      if (!t?.title) return recipe;
      const overrides: Record<string, any> = { title: t.title };
      if (t.tags) {
        const safeTags = normalizeTags(t.tags);
        if (safeTags.length > 0) overrides.tags = safeTags;
      }
      return { ...recipe, ...overrides };
    });
  }, [recipes, titlesMap, language]);
}

/** Returns true when a recipe row includes the fields needed for quality evaluation */
const hasFullQualityPayload = (recipe: unknown): boolean => {
  if (!recipe || typeof recipe !== "object") return false;
  const candidate = recipe as Record<string, unknown>;
  return "ingredients" in candidate && "preparation" in candidate;
};

/**
 * Fetches all persistent recipe translations for the current language.
 * Returns a Map<recipeId, translationFields>.
 */
export function useRecipeTranslationsMap() {
  const { language } = useLocale();

  return useQuery({
    queryKey: ["recipe-translations-map", language],
    queryFn: async () => {
      // Fetch first batch to determine total, then fire remaining pages in parallel.
      // This avoids the sequential while-loop that added ~500ms per extra page.
      const PAGE = 1000;
      const SELECT = "recipe_id, title, description, chef_tip, glycemic_notes, peak_time_text, glucose_rise_text, ingredients, preparation, tags";

      const { data: first, error: firstError, count } = await supabase
        .from("recipe_translations")
        .select(SELECT, { count: "exact" })
        .eq("language", language)
        .range(0, PAGE - 1);

      if (firstError) throw firstError;

      let rows: any[] = first || [];

      // If there are more pages, fetch them all in parallel
      if (count && count > PAGE) {
        const extraPages = Math.ceil((count - PAGE) / PAGE);
        const extraRequests = Array.from({ length: extraPages }, (_, i) => {
          const from = (i + 1) * PAGE;
          return supabase
            .from("recipe_translations")
            .select(SELECT)
            .eq("language", language)
            .range(from, from + PAGE - 1);
        });
        const extraResults = await Promise.all(extraRequests);
        for (const { data } of extraResults) {
          if (data) rows = rows.concat(data);
        }
      }

      const map = new Map<string, any>();
      rows.forEach((t: any) => map.set(t.recipe_id, t));
      return map;
    },
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

/**
 * Applies persistent translations to a list of recipes.
 * Returns the same array with translated fields overlaid when available.
 * 
 * IMPORTANT: Only applies COMPLETE translations (title + ingredients + preparation).
 * Incomplete translations are rejected to avoid mixed-language content.
 */
export function useLocalizedRecipes<T extends { id: string; language?: string; market?: string | null; cuisine_origin?: string | null; sort_order?: number | null }>(
  recipes: T[] | null | undefined,
  { skipQualityGate = false }: { skipQualityGate?: boolean } = {},
): T[] | null | undefined {
  const { language, market } = useLocale();
  const { data: translationsMap } = useRecipeTranslationsMap();

  return useMemo(() => {
    if (!recipes) return recipes;

    // Market is no longer used to hide recipes: every recipe is translated to
    // all languages, so recipe content is unified across markets. (Market is
    // still used below only to order by cuisine affinity, not to filter.)
    let result = recipes;

    if (translationsMap && translationsMap.size > 0) {
      result = result.map((recipe) => {
        const t = translationsMap.get(recipe.id);
        if (!t) return recipe;

        // Strict validation: require title + ingredients + preparation
        const hasIngredients = Array.isArray(t.ingredients) && t.ingredients.length > 0;
        const hasPreparation =
          (Array.isArray(t.preparation) && t.preparation.length > 0) ||
          (typeof t.preparation === "string" && t.preparation.trim().length > 0);
        const hasTitle = !!t.title;
        const isComplete = hasTitle && hasIngredients && hasPreparation;

        // Only apply COMPLETE translations to avoid mixed-language content
        if (!isComplete) {
          const missingFields: string[] = [];
          if (!hasTitle) missingFields.push("title");
          if (!hasIngredients) missingFields.push("ingredients");
          if (!hasPreparation) missingFields.push("preparation");
          return recipe;
        }

        // Campo nulo numa tradução completa SOBRESCREVE a base, de propósito.
        // O `if` que havia aqui deixava o texto do idioma-base atravessar:
        // 1.534 descrições e 1.505 dicas apareciam em alemão ou espanhol numa
        // tela inglesa. Campo vazio some; campo no idioma errado fica.
        const overrides: Record<string, any> = {
          description: t.description ?? null,
          chef_tip: t.chef_tip ?? null,
          glycemic_notes: t.glycemic_notes ?? null,
          peak_time_text: t.peak_time_text ?? null,
          glucose_rise_text: t.glucose_rise_text ?? null,
        };
        if (t.title) overrides.title = t.title;

        const rawIngredients = t.ingredients.map((ing: any) => {
          if (typeof ing === "string") return ing;
          const parts = [ing.quantity, ing.unit, ing.name].filter(Boolean).map((s: string) => s.trim()).filter(Boolean);
          return parts.join(" ") || ing.name || String(ing);
        });
        // Nome de unidade espanhola no idioma da tela. Antes isto só rodava
        // para o inglês, e 301 linhas chegavam em espanhol na tela alemã.
        overrides.ingredients = rawIngredients.map((l: string) => translateUnitsTo(l, language));

        const steps = Array.isArray(t.preparation)
          ? t.preparation.map((s: any) => typeof s === "string" ? s : s.text || String(s))
          : [t.preparation as string];
        overrides.preparation = steps.join("\n");

        if (t.tags) {
          const safeTags = normalizeTags(t.tags);
          if (safeTags.length > 0) overrides.tags = safeTags;
        }

        return { ...recipe, ...overrides };
      });
    }

    // Apply quality gate only to recipes that have full payloads (ingredients + preparation).
    // Lightweight preview rows (without those fields) pass through ungated.
    // Listing pages pass skipQualityGate=true to skip the gate entirely.
    const gated = skipQualityGate ? result : result.filter((r) => {
      if (!hasFullQualityPayload(r)) return true; // preview row — let it through
      const qr = evaluateRecipeQuality(r as any);
      if (!qr.pass) {
        // Telemetry is handled inside filterByQualityGate, but here we do per-recipe
        if (!_qualityReported.has(r.id)) {
          _qualityReported.add(r.id);
        }
        return false;
      }
      return true;
    });

    // Ordena por afinidade cultural: receitas do market preferido do user vêm primeiro
    const sorted = sortByCuisineAffinity(gated as T[], market);
    return sorted;
  }, [recipes, translationsMap, language, market, skipQualityGate]);
}

/** Normalize tags from various storage formats into string[] */
function normalizeTags(val: unknown): string[] {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter((v) => typeof v === "string" && v.trim());
  if (typeof val === "object" && val !== null) {
    const obj = val as Record<string, unknown>;
    if (Array.isArray(obj.custom)) return obj.custom.filter((v: any) => typeof v === "string" && v.trim());
    return [];
  }
  return [];
}
